// These are the slugs we use on get params
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { useTranslation } from 'react-i18next';
import { selectBidsMetaByTab } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { RootState } from 'Douleutaras/redux/store';
import { TypedUseSelectorHook } from 'react-redux';
import { JobBids, JSONApiDataObject } from 'Douleutaras/types/index';
import { BidAttributeTabIds, BidListStatuses, TabIds } from 'Douleutaras/views/seller-dashboard/jobs-list/types';
import { selectBidsWithOuterMetaByTab } from 'Douleutaras/redux/data/jobBidsWithOuter/jobBidsWithOuter';

// Maps "tab" attribute from Bid/Job coming from the API and translates it to our tab list
export const mapTabAttribute = (BidListTabOption): TabIds => {
  switch (BidListTabOption) {
    default:
    case BidAttributeTabIds.ALL:
      return TabIds.ALL;
    case BidAttributeTabIds.RECENTLY_ASSIGNED:
      return TabIds.NEW;
    case BidAttributeTabIds.SET_FOR_TODAY:
      return TabIds.TODAY;
    case BidAttributeTabIds.NEXT_7_DAYS:
      return TabIds.NEXT;
    case BidAttributeTabIds.REST:
      return TabIds.LATER;
    case BidAttributeTabIds.CONCLUDED_BY_PRO:
    case BidAttributeTabIds.CANCELLED_BY_PRO:
      return TabIds.BY_YOU;
    case BidAttributeTabIds.CONCLUDED_BY_ANOTHER_PRO:
      return TabIds.BY_ANOTHER_PROFESSIONAL;
    case BidAttributeTabIds.CONCLUDED_OUTSIDE:
      return TabIds.OUTSIDE_DOULEUTARAS;
    case BidAttributeTabIds.CANCELLED_BY_CLIENT:
      return TabIds.BY_THE_CUSTOMER;
  }
};

interface TabConfigBids {
  title: string;
  currentTabBids: Array<JSONApiDataObject<JobBids>>;
  tabId: TabIds;
  count: number;
}

export const useAllTabs = (status, filterJobStatuses, filterJobCategory, filterJobIntent): [ Array<TabConfigBids>, Array<string> ] => {
  const { t } = useTranslation();
  const counters = useAppSelector(state => state.counters);

  const currentTabs = [
    {
      title: t('T.JOBS-LIST.TAB.All'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.ALL, status })),
      tabId: TabIds.ALL,
      count: counters.all
    },
    {
      title: t('T.JOBS-LIST.TAB.NEW'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.NEW, status })),
      tabId: TabIds.NEW,
      count: counters.recently_assigned
    },
    {
      title: t('T.JOBS-LIST.TAB.TODAY'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.TODAY, status })),
      tabId: TabIds.TODAY,
      count: counters.set_for_today
    },
    {
      title: t('T.JOBS-LIST.TAB.NEXT'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.NEXT, status })),
      tabId: TabIds.NEXT,
      count: counters.next_7_days
    },
    {
      title: t('T.JOBS-LIST.TAB.LATER'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        // tslint:disable-next-line:no-null-keyword
        { tab: TabIds.LATER, status })),
      tabId: TabIds.LATER,
      count: counters.rest
    }
  ];

  const concludedTabs = [
    {
      title: t('T.JOBS-LIST.TAB.All'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.ALL, status })),
      tabId: TabIds.ALL,
      count: counters.all
    },
    {
      title: t('T.JOBS-LIST.TAB.CONCLUDED BY YOU'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.BY_YOU, status })),
      tabId: TabIds.BY_YOU,
      count: counters.concluded_by_pro
    },
    {
      title: t('T.JOBS-LIST.TAB.CONCLUDED BY ANOTHER PROFESSIONAL'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.BY_ANOTHER_PROFESSIONAL, status })),
      tabId: TabIds.BY_ANOTHER_PROFESSIONAL,
      count: counters.concluded_by_other_pro
    },
    {
      title: t('T.JOBS-LIST.TAB.CONCLUDED OUTSIDE DOULEUTARAS'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.OUTSIDE_DOULEUTARAS, status })),
      tabId: TabIds.OUTSIDE_DOULEUTARAS,
      count: counters.concluded_outside
    }
  ];

  const cancelledTabs = [
    {
      title: t('T.JOBS-LIST.TAB.All'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.ALL, status })),
      tabId: TabIds.ALL,
      count: counters.all
    },
    {
      title: t('T.JOBS-LIST.TAB.CANCELLED BY YOU'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.BY_YOU, status })),
      tabId: TabIds.BY_YOU,
      count: counters.cancelled_by_pro
    },
    {
      title: t('T.JOBS-LIST.TAB.CANCELLED BY THE CUSTOMER'),
      currentTabBids: useAppSelector(state => selectBidsWithOuterMetaByTab(state,
        { tab: TabIds.BY_THE_CUSTOMER, status })),
      tabId: TabIds.BY_THE_CUSTOMER,
      count: counters.cancelled_by_client
    }
  ];

  switch (status) {
    default:
    case BidListStatuses.CURRENT:
      return [ currentTabs, currentTabs.map(currentTab => currentTab.tabId) ];
    case BidListStatuses.CONCLUDED:
      return [ concludedTabs, concludedTabs.map(currentTab => currentTab.tabId) ];
    case BidListStatuses.CANCELLED:
      return [ cancelledTabs, cancelledTabs.map(currentTab => currentTab.tabId) ];
  }
};
